import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Card } from '@w7/ui'
import { bookmakerApi, bmErrorMessage } from '@/api/bookmaker'
import { BmPage } from './BmPage'
import './create-forms.css'

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

function firstEntity(data: unknown): Record<string, unknown> {
  if (Array.isArray(data)) return asRecord(data[0])
  const o = asRecord(data)
  if (Array.isArray(o.result)) return asRecord(o.result[0])
  return o
}

export function CreateGamePage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [name, setName] = useState('')
  const [isMatchOdds, setIsMatchOdds] = useState('true')
  const [entity, setEntity] = useState<Record<string, unknown>>({})

  useEffect(() => {
    if (!id) return
    void (async () => {
      setBusy(true)
      try {
        const row = firstEntity(await bookmakerApi.gameGetOne({ id }))
        setEntity(row)
        setName(String(row.name ?? ''))
        setIsMatchOdds(String(row.isMatchOdds ?? 'true'))
      } catch (e) {
        setError(bmErrorMessage(e))
      } finally {
        setBusy(false)
      }
    })()
  }, [id])

  async function onSubmit() {
    if (!name.trim()) {
      setError('Insert Valid Name')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        isMatchOdds: isMatchOdds === 'true' || isMatchOdds === 'True',
      }
      if (isEdit) {
        await bookmakerApi.gameEdit({
          ...entity,
          ...body,
          _id: id,
          id: entity.id ?? id,
        })
      } else {
        // Lab GameModel.create assigns random 9-digit id
        await bookmakerApi.gameCreate(body)
      }
      navigate('/game')
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <BmPage
      title={isEdit ? 'Edit Game' : 'Create Game'}
      description="Lab create game — API assigns a new 9-digit game id"
      error={error}
    >
      <Card>
        <div className="bm-create">
          <div className="bm-create-row">
            <label htmlFor="bm-game-name">Game Name</label>
            <input
              id="bm-game-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Insert Game Name"
            />
          </div>
          <div className="bm-create-row">
            <label htmlFor="bm-game-mo">isMatchOdds</label>
            <select
              id="bm-game-mo"
              value={isMatchOdds}
              onChange={(e) => setIsMatchOdds(e.target.value)}
            >
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          </div>
          <div className="bm-create-actions">
            <Button type="button" disabled={busy} onClick={() => void onSubmit()}>
              {busy ? 'Working…' : 'Submit'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate('/game')}>
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </BmPage>
  )
}
